import { useCallback, useRef } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * Telemetry event emitter per TELEMETRY_CONTRACT.md
 *
 * Events are sent to the backend telemetry endpoint and also logged to console
 * in development mode for debugging.
 *
 * Required properties on all events:
 * - tenant_id
 * - user_id
 * - event_name
 * - timestamp
 *
 * Optional properties:
 * - entity_type, entity_id, metadata
 *
 * IDI-303 (2026-09-11): fixed two bugs that meant NO telemetry event of any
 * kind had ever successfully reached the backend, confirmed by reading both
 * sides together:
 *   1. Wrong endpoint. This pointed at `/api/telemetry`; the real route
 *      (terraform/main.tf) is `/v1/telemetry`, behind the API Gateway's
 *      Cognito JWT authorizer — `/api/telemetry` doesn't exist and 404s.
 *   2. No auth header. Even fixing (1), the telemetry route requires a
 *      validated JWT (WithTenantExtraction returns 401 with no token) —
 *      this used a bare `fetch()` with no Authorization header at all,
 *      unlike services/api.js's axios client which attaches one via
 *      fetchAuthSession(). Added the same token-attachment here.
 * See cmd/telemetry/main.go's `emitEventRequest` for the corresponding
 * backend-side fix (it only ever accepted a single flat event; the frontend
 * has always sent `{ events: [...] }` batches).
 */

const TELEMETRY_ENDPOINT = process.env.REACT_APP_TELEMETRY_URL || '/v1/telemetry';

// getUserContext (IDI-303) — extracted to module scope so both the
// useTelemetry hook and reportError (below, usable OUTSIDE a hook: class
// component error boundaries, window.onerror) share one implementation.
function getTelemetryUserContext() {
  try {
    const token = localStorage.getItem('authToken');
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        tenant_id: payload.tenant_id || payload['custom:tenant_id'] || 'unknown',
        user_id: payload.sub || 'unknown',
      };
    }
  } catch (e) {
    // Ignore parse errors
  }
  return { tenant_id: 'unknown', user_id: 'unknown' };
}

// sendTelemetryBatch (IDI-303) — the ONE place that actually POSTs to the
// backend. Attaches the same idToken services/api.js's axios interceptor
// uses, since the telemetry route sits behind the same Cognito JWT
// authorizer as every other /v1/* route (terraform/main.tf).
async function sendTelemetryBatch(events) {
  if (!Array.isArray(events) || events.length === 0) return;
  if (!navigator.onLine) return;
  try {
    let token;
    try {
      const session = await fetchAuthSession({ forceRefresh: false });
      token = session.tokens?.idToken?.toString();
    } catch (authErr) {
      // No session (logged out, session expired) — telemetry is genuinely
      // unauthenticated at this point; sending would just 401. Drop it
      // rather than throw, matching the existing "telemetry is best-effort"
      // contract.
      return;
    }
    if (!token) return;
    await fetch(TELEMETRY_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ events }),
      keepalive: true,
    });
  } catch (e) {
    // Silently fail — telemetry must never throw or block the caller.
  }
}

// Event names per TELEMETRY_CONTRACT.md
export const EVENTS = {
  // Navigation
  PAGE_VIEW: 'page_view',
  TAB_SWITCH: 'tab_switch',

  // Quote lifecycle
  QUOTE_CREATED: 'quote_created',
  QUOTE_SAVED: 'quote_saved',
  QUOTE_SENT: 'quote_sent',
  QUOTE_ACCEPTED: 'quote_accepted',
  QUOTE_CHANGES_REQUESTED: 'quote_changes_requested',
  QUOTE_CONVERTED: 'quote_converted_to_job',

  // Invoice lifecycle
  INVOICE_CREATED: 'invoice_created',
  INVOICE_SENT: 'invoice_sent',
  INVOICE_PAID: 'invoice_paid',
  INVOICE_EXPORTED_QBO: 'invoice_exported_qbo',

  // Customer
  CUSTOMER_CREATED: 'customer_created',
  CUSTOMER_UPDATED: 'customer_updated',

  // Job
  JOB_CREATED: 'job_created',
  JOB_COMPLETED: 'job_completed',

  // Settings
  SETTINGS_UPDATED: 'settings_updated',

  // Offline
  OFFLINE_QUEUE_FLUSH: 'offline_queue_flush',
  CONNECTIVITY_CHANGE: 'connectivity_change',

  // Errors
  ERROR_OCCURRED: 'error_occurred',
  API_ERROR: 'api_error',
};

function useTelemetry() {
  const queueRef = useRef([]);
  const flushTimerRef = useRef(null);

  const getUserContext = useCallback(getTelemetryUserContext, []);

  const flush = useCallback(async () => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }

    const events = [...queueRef.current];
    queueRef.current = [];
    await sendTelemetryBatch(events);
  }, []);

  const emit = useCallback((eventName, properties = {}) => {
    const context = getUserContext();

    // Reject events without required fields
    if (!context.tenant_id || !context.user_id) {
      console.warn('[Telemetry] Event rejected: missing tenant_id or user_id', eventName);
      return;
    }

    const event = {
      event_name: eventName,
      timestamp: new Date().toISOString(),
      tenant_id: context.tenant_id,
      user_id: context.user_id,
      ...properties,
    };

    // Log in development
    if (process.env.NODE_ENV === 'development') {
      console.debug('[Telemetry]', eventName, event);
    }

    // Add to batch queue
    queueRef.current.push(event);

    // Flush after 5 seconds or when batch reaches 10 events
    if (queueRef.current.length >= 10) {
      flush();
    } else if (!flushTimerRef.current) {
      flushTimerRef.current = setTimeout(flush, 5000);
    }
  }, [getUserContext, flush]);

  const trackPageView = useCallback((pageName, metadata = {}) => {
    emit(EVENTS.PAGE_VIEW, {
      entity_type: 'page',
      entity_id: pageName,
      metadata: { url: window.location.pathname, ...metadata },
    });
  }, [emit]);

  const trackAction = useCallback((eventName, entityType, entityId, metadata = {}) => {
    emit(eventName, { entity_type: entityType, entity_id: entityId, metadata });
  }, [emit]);

  return {
    emit,
    flush,
    trackPageView,
    trackAction,
    EVENTS,
  };
}

export default useTelemetry;

// reportError (IDI-303) — standalone, hook-free error reporter. Before this,
// EVENTS.ERROR_OCCURRED/API_ERROR existed in the contract but were NEVER
// emitted anywhere in the codebase — confirmed via repo-wide grep. This is
// the actual wiring: usable from class-component error boundaries
// (componentDidCatch cannot call a hook) and from a global
// window.onerror/unhandledrejection listener (see reportGlobalErrors below),
// neither of which can use the `emit` returned by useTelemetry().
//
// Fires immediately (no batching/debounce) — an error is not something we
// want delayed behind the normal telemetry flush interval, and a page crash
// may tear the tab down before a queued flush would ever run.
//
// PII: does NOT send the raw error message/stack by default beyond what
// `error.message` naturally contains (component name, a thrown Error's own
// text) — callers are responsible for not putting customer PII into thrown
// Error messages. This mirrors the existing emit() contract, which already
// sends whatever `metadata` a caller supplies.
export function reportError(source, error, extra = {}) {
  try {
    const context = getTelemetryUserContext();
    const event = {
      event_name: EVENTS.ERROR_OCCURRED,
      timestamp: new Date().toISOString(),
      tenant_id: context.tenant_id,
      user_id: context.user_id,
      entity_type: 'error',
      entity_id: source,
      metadata: {
        message: error?.message || String(error),
        stack: error?.stack ? String(error.stack).slice(0, 2000) : undefined,
        url: window.location.pathname,
        ...extra,
      },
    };
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('[Telemetry] reportError:', event);
    }
    sendTelemetryBatch([event]);
  } catch (e) {
    // Never let the error reporter itself throw.
  }
}

// reportApiError (IDI-303) — the API_ERROR sibling of reportError, for
// wiring into the shared axios error-handling path (services/api.js) so
// every failed request — not just render crashes — is visible in
// telemetry/alerting, not only in the per-call catch block's local UI state.
export function reportApiError(url, status, message, extra = {}) {
  try {
    const context = getTelemetryUserContext();
    const event = {
      event_name: EVENTS.API_ERROR,
      timestamp: new Date().toISOString(),
      tenant_id: context.tenant_id,
      user_id: context.user_id,
      entity_type: 'api_error',
      entity_id: url,
      metadata: { status, message, ...extra },
    };
    sendTelemetryBatch([event]);
  } catch (e) {
    // Never let the error reporter itself throw.
  }
}

// reportGlobalErrors (IDI-303) — call ONCE at app startup (src/index.js) to
// catch errors React's own error boundaries never see: anything outside a
// render (an event handler that throws without a try/catch, a rejected
// Promise nobody awaited/caught, a syntax/runtime error in a script tag).
// React error boundaries (RouteErrorBoundary, ChartErrorBoundary) only catch
// render-phase errors in their subtree — this is the net underneath them.
export function reportGlobalErrors() {
  window.addEventListener('error', (event) => {
    reportError('window.onerror', event.error || new Error(event.message));
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    reportError('unhandledrejection', reason instanceof Error ? reason : new Error(String(reason)));
  });
}
